import { CanvasRenderingContext2D, createCanvas, ImageData, Image} from 'canvas';
import { Img, IType, Renderer, registerRendererType, arrayBufferToImage } from '@p4b/image-base';
import { Transform } from '@p4b/utils';

//------------------------------------------------------------------------
// Support for standard web image formats

type StdImg = Img;
//export interface StdImg extends Img {}

export function makeStd(id: string, data: ArrayBuffer[]): StdImg {
    return {
        id,
        iType: IType.Std,
        frames: data.map(buf => ({
            data: buf,
            dataSize: buf.byteLength,
        })),
        frameCount: data.length,
    };
}

export function isStdImg(img: Img): img is StdImg {
    return img.iType === IType.Std;
}

export class StdRenderer implements Renderer {
    public index: number;
    public readonly img: StdImg;
    private image?: Image;

    public constructor(img: StdImg) {
        this.index = 0;
        this.img = img;
    }

    public async init(): Promise<void> {}

    public destroy(): void {}

    public async render(): Promise<void>{}

    public async renderThumbnail(): Promise<ImageData> {
        const frame = this.img.frames[this.index];
        if (!frame.data) {
            throw 'no image data.';
        } else {
            const img = await arrayBufferToImage(frame.data);
            this.img.cols = img.width;
            this.img.rows = img.height;
            const canvas = createCanvas(120 * img.width / img.height, 120);
            const cxt = canvas.getContext('2d', { alpha: false });
            if (!cxt) {
                throw 'null context';
            }
            cxt.imageSmoothingEnabled = true;
            if ('imageSmoothingQuality' in cxt) {
                cxt.imageSmoothingQuality = 'high';
            }
            cxt.drawImage(img, 0, 0, canvas.width, canvas.height);
            return cxt.getImageData(0, 0, canvas.width, canvas.height);
        }
    }

    public async animationFrame(context: CanvasRenderingContext2D, t: Transform): Promise<void> {
        //context.clearRect(0, 0, context.canvas.width, context.canvas.height);
        context.fillStyle = 'black';
        context.fillRect(0, 0, context.canvas.width, context.canvas.height);
        if (this.image && this.image.width && this.image.height) {
            context.setTransform(t.s, t.r, -t.r, t.s, t.tx, t.ty);
            context.imageSmoothingEnabled = true;
            if ('imageSmoothingQuality' in context) {
                context.imageSmoothingQuality = 'high';
            }
            context.drawImage(this.image, 0, 0);
            //    Math.floor(context.canvas.width / 2 - this.image.width / 2),
            //    Math.floor(context.canvas.height / 2 - this.image.height / 2),
            //);
        }
    }

    public async load(
        resources: {
            getImageBegin(): Promise<void>;
            getImageFrame(start: number, end: number): Promise<ArrayBuffer|undefined>;
            getImageEnd(): Promise<void>;
        },
        render: () => Promise<void>,
        progress: (p: number) => void,
    ): Promise<void> {
        let p = 100.0;
        const p_step = 100.0 / (this.img.frameCount + 1);
        progress(p);
        await resources.getImageBegin();
        let j = this.index;
        for (let i = 0; i < this.img.frameCount; ++i) {
            j = (j === this.img.frameCount) ? 0 : j;
            const frame = this.img.frames[j];
            if (frame.dbOffset != undefined) {
                console.debug('STD GET_FRAME');
                frame.data = await resources.getImageFrame(frame.dbOffset, frame.dbOffset + frame.dataSize);
                p -= p_step;
                progress(p);
            }
            if (j === this.index && frame.data !== undefined) {
                console.debug('STD TO IMG');
                this.image = await arrayBufferToImage(frame.data);
                p -= p_step;
                progress(p);
                this.img.cols = this.image.width;
                this.img.rows = this.image.height;
                await render();
            }
            ++j;
        }
        await resources.getImageEnd();
        console.debug('STD LOAD DONE');
        progress(0.0);
    }

    public convexMean(): {mean?: number, stddev?: number} {
        return {};
    }
}

export function isStdRenderer(renderer: Renderer): renderer is StdRenderer {
    return isStdImg(renderer.img);
}

registerRendererType({
    name: 'StdRenderer',
    hasMime(mime: string) {
        return mime === 'image/apng' ||
            mime === 'image/bmp' ||
            mime === 'image/gif' ||
            mime === 'image/x-icon' ||
            mime === 'image/jpeg' ||
            mime === 'image/png' ||
            mime === 'image/svg+xml' ||
            mime === 'image/webp';
    },
    async makeImg(id: string, buffer: ArrayBuffer): Promise<Img> {
        return makeStd(id, [buffer]);
    },
    isThis(resource: Img) {
        return resource.iType === IType.Std;
    },
    makeRenderer(resource: StdImg): Renderer {
        return new StdRenderer(resource);
    }
});